import { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import api from "@/utils/api";
import { toast } from "sonner";
import { subDays, format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import {
  Warning,
  ArrowsClockwise,
  DownloadSimple,
  UploadSimple,
  CheckCircle,
  Clock,
  Lightning,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import DateRangePicker from "@/components/DateRangePicker";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Colors matching the reference design
const EXPENSE_COLORS = {
  commission: "#ec4899",       // Pink/Magenta - Комиссия МП
  logistics: "#f472b6",        // Light pink - Логистика доставки  
  returns_logistics: "#60a5fa", // Blue - Логистика возвратов
  advertising: "#fb923c",      // Orange - Рекламные затраты
  acquiring: "#a78bfa",        // Light purple - Эквайринг
  cost_price: "#fbbf24",       // Yellow/Gold - Себестоимость
  tax: "#8b5cf6",              // Purple - Расчетный налог
};

// Format money with Russian locale
const formatMoney = (value) => {
  if (!value && value !== 0) return "0 ₽";
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatMoneyShort = (value) => {
  if (!value && value !== 0) return "0";
  if (Math.abs(value) >= 1000000) {
    return (value / 1000000).toFixed(1) + " млн";
  }
  if (Math.abs(value) >= 1000) {
    return (value / 1000).toFixed(0) + " тыс";
  }
  return value.toFixed(0);
};

const formatPercent = (value) => {
  if (!value && value !== 0) return "0%";
  return value.toFixed(0) + " %";
};

// Custom tooltip for bar chart
const CustomBarTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 shadow-xl">
        <p className="text-white font-medium">{payload[0].payload.name}</p>
        <p className="text-zinc-300">{formatMoney(Math.abs(payload[0].value))}</p>
      </div>
    );
  }
  return null;
};

// Sync status indicator component
const SyncStatusBadge = ({ syncStatus, syncing }) => {
  if (!syncStatus) return null;
  
  const status = syncStatus.status;
  const elapsedMinutes = syncStatus.elapsed_minutes || 0;
  
  // In progress - check elapsed time
  if (status === "in_progress" || syncing) {
    // If running for more than 5 minutes, show different message
    if (elapsedMinutes > 5) {
      return (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-500/30 rounded-full">
          <ArrowsClockwise size={14} className="text-amber-400" />
          <span className="text-xs text-amber-400">
            Синхронизация в фоне ({Math.round(elapsedMinutes)} мин). Вы можете вернуться позже.
          </span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 border border-blue-500/30 rounded-full">
        <ArrowsClockwise size={14} className="text-blue-400 animate-spin" />
        <span className="text-xs text-blue-400">{syncStatus.progress || "Синхронизация..."}</span>
      </div>
    );
  }
  
  // Completed successfully
  if (status === "completed" || status === "success") {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/30 rounded-full">
        <CheckCircle size={14} className="text-emerald-400" />
        <span className="text-xs text-emerald-400">
          Данные от: {syncStatus.last_sync_msk}
        </span>
      </div>
    );
  }
  
  // Completed with warnings (FBS data may be delayed)
  if (status === "completed_with_warnings") {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-500/30 rounded-full">
        <CheckCircle size={14} className="text-amber-400" />
        <span className="text-xs text-amber-400">
          Данные от: {syncStatus.last_sync_msk} (FBS данные обновятся позже)
        </span>
      </div>
    );
  }
  
  // Failed
  if (status === "failed" || status === "error") {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 border border-red-500/30 rounded-full">
        <Warning size={14} className="text-red-400" />
        <span className="text-xs text-red-400">
          Ошибка синхронизации. Попробуйте снова.
        </span>
      </div>
    );
  }
  
  // Timeout
  if (status === "timeout") {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-500/30 rounded-full">
        <Warning size={14} className="text-amber-400" />
        <span className="text-xs text-amber-400">
          Превышено время ожидания. Попробуйте снова.
        </span>
      </div>
    );
  }
  
  // Never synced
  if (status === "never_synced") {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-500/30 rounded-full">
        <Warning size={14} className="text-amber-400" />
        <span className="text-xs text-amber-400">Требуется синхронизация</span>
      </div>
    );
  }
  
  return null;
};

// Default date range - last 30 days
const getDefaultDateRange = () => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const from = subDays(today, 30);
  from.setHours(0, 0, 0, 0);
  return { from, to: today };
};

export default function Dashboard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === "admin" || user?.is_admin;
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [financeData, setFinanceData] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [uploadingCosts, setUploadingCosts] = useState(false);
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [allTransactions, setAllTransactions] = useState([]);
  const fileInputRef = useRef(null);

  // Fetch data from fast API
  const fetchData = async () => {
    setLoading(true);
    try {
      // First check sync status
      const [statusRes, dashboardRes, financeRes] = await Promise.all([
        api.get(`/sync/status`),
        api.get(`/fast/dashboard`),
        api.get(`/fast/finance?limit=10000`)
      ]);
      
      setSyncStatus(statusRes.data);
      
      // If sync is in progress, start polling
      if (statusRes.data.status === "in_progress") {
        setSyncing(true);
      }
      
      if (dashboardRes.data.synced) {
        setAllProducts(dashboardRes.data.products || []);
        // Store all transactions for date filtering
        setAllTransactions(financeRes.data.transactions_by_sku || {});
      } else {
        // No synced data - show message
        setFinanceData(null);
        setAllProducts([]);
      }
      
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Ошибка загрузки данных");
    } finally {
      setLoading(false);
    }
  };

  // Filter and calculate metrics based on date range
  const filteredData = useMemo(() => {
    if (!allProducts.length || !dateRange?.from || !dateRange?.to) {
      return { products: [], totals: {}, metrics: {} };
    }
    
    const fromDate = format(dateRange.from, "yyyy-MM-dd");
    const toDate = format(dateRange.to, "yyyy-MM-dd");
    
    // Filter transactions by date and recalculate totals
    const productStats = {};
    
    // Go through all transactions and filter by date
    Object.entries(allTransactions).forEach(([sku, transactions]) => {
      const filtered = transactions.filter(t => {
        const txDate = t.operation_date?.split(" ")[0] || t.operation_date?.split("T")[0];
        return txDate >= fromDate && txDate <= toDate;
      });
      
      if (filtered.length > 0) {
        // Find product info
        const productInfo = allProducts.find(p => String(p.sku) === String(sku)) || {};
        
        // Calculate stats from filtered transactions
        const stats = {
          sku: parseInt(sku),
          name: productInfo.name || "",
          offer_id: productInfo.offer_id || "",
          image_url: productInfo.image_url || "",
          quantity_sold: 0,
          returns_count: 0,
          revenue: 0,
          commission: 0,
          logistics: 0,
          returns_cost: 0,
          advertising: 0,
          acquiring: 0,
          cost_price: 0,
          customer_revenue: 0,
          tax_usn: 0,
          profit: 0,
          margin: 0
        };
        
        filtered.forEach(t => {
          // New format: operation_type = "sale" | "return" | "nevykup"
          const txType = t.operation_type || "";
          const isReturn = txType === "return";
          const isNevykup = txType === "nevykup";
          const isSale = txType === "sale";
          
          if (isReturn || isNevykup) {
            stats.returns_count += 1;
            stats.returns_cost += (t.logistics_return || 0) + (t.logistics_delivery || 0);
          } else if (isSale) {
            stats.quantity_sold += 1;
            stats.revenue += t.accruals_for_sale || 0;
            stats.commission += Math.abs(t.sale_commission || 0);
            stats.logistics += t.logistics_delivery || 0;
            stats.acquiring += Math.abs(t.acquiring_fee || 0);
          }
        });
        
        // Calculate proportional values from product totals (only for cost_price and advertising)
        if (productInfo.quantity_sold && productInfo.quantity_sold > 0) {
          const ratio = stats.quantity_sold / productInfo.quantity_sold;
          stats.cost_price = (productInfo.cost_price || 0) * ratio;
          stats.advertising = (productInfo.advertising || 0) * ratio;
        }
        
        // Calculate customer revenue and tax (USN 6%)
        const customerCoef = productInfo.customer_coefficient || 0.6;
        stats.customer_revenue = stats.revenue * customerCoef;
        stats.tax_usn = stats.customer_revenue * 0.06;
        
        // Calculate profit
        stats.profit = stats.revenue - stats.commission - stats.logistics - stats.returns_cost 
          - stats.advertising - stats.acquiring - stats.cost_price - stats.tax_usn;
        
        // Margin
        if (stats.revenue > 0) {
          stats.margin = (stats.profit / stats.revenue) * 100;
        }
        
        productStats[sku] = stats;
      }
    });
    
    const products = Object.values(productStats).sort((a, b) => b.revenue - a.revenue);
    
    // Calculate totals
    const totals = products.reduce((acc, p) => ({
      revenue: acc.revenue + p.revenue,
      commission: acc.commission + p.commission,
      logistics: acc.logistics + p.logistics,
      returns_cost: acc.returns_cost + p.returns_cost,
      advertising: acc.advertising + p.advertising,
      acquiring: acc.acquiring + p.acquiring,
      cost_price: acc.cost_price + p.cost_price,
      tax_usn: acc.tax_usn + p.tax_usn,
      profit: acc.profit + p.profit,
      orders_count: acc.orders_count + p.quantity_sold,
      returns_count: acc.returns_count + p.returns_count
    }), {
      revenue: 0, commission: 0, logistics: 0, returns_cost: 0,
      advertising: 0, acquiring: 0, cost_price: 0, tax_usn: 0,
      profit: 0, orders_count: 0, returns_count: 0
    });
    
    if (totals.revenue > 0) {
      totals.margin = (totals.profit / totals.revenue) * 100;
    } else {
      totals.margin = 0;
    }
    
    // Calculate metrics
    const negativeMarginProducts = products.filter(p => p.margin < 0);
    const noCostProducts = products.filter(p => !p.cost_price || p.cost_price === 0);
    const lowMarginProducts = products.filter(p => p.margin >= 0 && p.margin < 20);
    
    const metrics = {
      negative_margin_count: negativeMarginProducts.length,
      negative_margin_loss: negativeMarginProducts.reduce((sum, p) => sum + Math.abs(p.profit), 0),
      no_cost_count: noCostProducts.length,
      low_margin_count: lowMarginProducts.length
    };
    
    return { products, totals, metrics };
  }, [allProducts, allTransactions, dateRange]);

  // Invalidate React Query cache after successful sync
  const invalidateCacheAfterSync = () => {
    // Сбрасываем кэш для всех основных разделов
    queryClient.invalidateQueries({ queryKey: ["stocks"] });
    queryClient.invalidateQueries({ queryKey: ["clusters"] });
    queryClient.invalidateQueries({ queryKey: ["finance"] });
    queryClient.invalidateQueries({ queryKey: ["productStats"] });
    queryClient.invalidateQueries({ queryKey: ["settings"] });
    queryClient.invalidateQueries({ queryKey: ["syncStatus"] });
    queryClient.invalidateQueries({ queryKey: ["turnover"] });
    console.log("[CACHE] All query caches invalidated after sync");
  };

  // Poll sync status while syncing
  useEffect(() => {
    let interval;
    if (syncing) {
      interval = setInterval(async () => {
        try {
          const res = await api.get(`/sync/status`);
          setSyncStatus(res.data);
          
          const status = res.data.status;
          const finishedStatuses = ["completed", "completed_with_warnings", "success", "failed", "error", "timeout"];
          
          if (finishedStatuses.includes(status)) {
            setSyncing(false);
            
            if (status === "completed" || status === "success") {
              toast.success("Синхронизация завершена!");
              invalidateCacheAfterSync(); // Сброс кэша
              fetchData();
            } else if (status === "completed_with_warnings") {
              toast.success("Синхронизация завершена. Некоторые FBS-данные обновятся позже.");
              invalidateCacheAfterSync(); // Сброс кэша
              fetchData();
            } else if (status === "timeout") {
              toast.warning("Превышено время ожидания. Попробуйте снова.");
            } else {
              toast.error(`Ошибка синхронизации: ${res.data.error || "Неизвестная ошибка"}`);
            }
          }
        } catch (e) {
          console.error("Error polling sync status:", e);
        }
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [syncing]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success("Данные обновлены");
  };

  // Reset stuck sync
  const handleResetSync = async () => {
    try {
      const res = await api.post(`/sync/reset`);
      toast.info(res.data.message || "Статус синхронизации сброшен");
      setSyncing(false);
      await fetchData();
    } catch (error) {
      toast.error("Ошибка сброса синхронизации");
    }
  };

  // Trigger manual sync (admin only)
  const handleSync = async () => {
    // Check if user is admin
    if (!isAdmin) {
      toast.info("Ручная синхронизация доступна только администраторам. Автоматическая синхронизация выполняется ежедневно в 01:00 по Москве.");
      return;
    }
    
    setSyncing(true);
    try {
      const res = await api.post(`/sync/manual`);
      
      if (res.data.status === "cooldown") {
        setSyncing(false);
        toast.warning(res.data.message);
      } else if (res.data.status === "in_progress") {
        toast.info("Синхронизация уже выполняется...");
      } else {
        toast.info("Синхронизация запущена...");
      }
    } catch (error) {
      setSyncing(false);
      if (error.response?.status === 403) {
        toast.info("Ручная синхронизация доступна только администраторам.");
      } else {
        toast.error("Ошибка запуска синхронизации");
      }
    }
  };

  // Download costs template with fallback method
  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      // Use fetch to download and create blob for reliable file download
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/finance/costs/template`, {
        method: 'GET',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        throw new Error('Ошибка скачивания');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'cost_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Шаблон скачан!");
      
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Ошибка скачивания шаблона. Попробуйте ещё раз.");
    } finally {
      setTimeout(() => setDownloadingTemplate(false), 500);
    }
  };

  // Upload costs file
  const handleUploadCosts = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setUploadingCosts(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(`/finance/costs/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { updated, skipped } = response.data;
      
      if (updated > 0) {
        toast.success(
          <div>
            <div className="font-medium">Себестоимость загружена!</div>
            <div className="text-xs text-zinc-400 mt-1">
              Обновлено товаров: <span className="text-green-400">{updated}</span>
            </div>
            {skipped > 0 && (
              <div className="text-xs text-zinc-500">
                Пропущено (без новой цены): {skipped}
              </div>
            )}
          </div>,
          { duration: 5000 }
        );
        // Trigger re-sync to recalculate profits
        toast.info("Запускаем пересчёт данных...");
        handleSync();
      } else {
        toast.warning(
          <div>
            <div className="font-medium">Данные не загружены</div>
            <div className="text-xs text-zinc-400 mt-1">
              Заполните колонку &quot;НОВАЯ СЕБЕСТОИМОСТЬ&quot; в файле
            </div>
          </div>,
          { duration: 5000 }
        );
      }
      
    } catch (error) {
      console.error("Error uploading costs:", error);
      toast.error("Ошибка загрузки файла. Проверьте формат файла.");
    } finally {
      setUploadingCosts(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Use filtered data based on date range
  const { totals, metrics, products } = filteredData;
  const revenue = totals.revenue || 0;
  const profit = totals.profit || 0;
  const totalExpenses = revenue - profit;
  const profitMargin = revenue > 0 ? (profit / revenue) * 100 : 0;
  
  // Chart data for bar chart
  const barChartData = [
    { name: "Выручка", value: revenue, displayValue: revenue },
    { name: "Итого затраты", value: totalExpenses, displayValue: -totalExpenses },
    { name: "Маржин. прибыль", value: profit, displayValue: profit },
  ];

  // Pie chart data for expenses structure
  const expensesData = [
    { name: "Комиссия МП", value: Math.abs(totals.commission || 0), color: EXPENSE_COLORS.commission },
    { name: "Логистика доставки", value: Math.abs(totals.logistics || 0), color: EXPENSE_COLORS.logistics },
    { name: "Логистика возвратов", value: Math.abs(totals.returns_cost || 0), color: EXPENSE_COLORS.returns_logistics },
    { name: "Рекламные затраты", value: Math.abs(totals.advertising || 0), color: EXPENSE_COLORS.advertising },
    { name: "Эквайринг", value: Math.abs(totals.acquiring || 0), color: EXPENSE_COLORS.acquiring },
    { name: "Себестоимость", value: Math.abs(totals.cost_price || 0), color: EXPENSE_COLORS.cost_price },
    { name: "Расчетный налог", value: Math.abs(totals.tax_usn || 0), color: EXPENSE_COLORS.tax },
  ].filter(item => item.value > 0);

  // Bar colors
  const getBarFill = (name) => {
    switch (name) {
      case "Выручка": return "#3b82f6";
      case "Итого затраты": return "#a855f7";
      case "Маржин. прибыль": return "#22c55e";
      default: return "#6b7280";
    }
  };

  // Check if we have synced data
  const hasSyncedData = allProducts.length > 0 || Object.keys(allTransactions).length > 0;

  return (
    <div className="-m-6 md:-m-8 lg:-m-12 min-h-[calc(100vh-64px)] bg-[#1a1d24] text-white" data-testid="dashboard-page">
      {/* Header */}
      <div className="px-6 py-4 border-b border-zinc-800">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold text-white">Маржинальная прибыль</h1>
            <SyncStatusBadge syncStatus={syncStatus} syncing={syncing} />
          </div>
          
          <div className="flex items-center gap-3">
            {/* Date Range Picker */}
            <DateRangePicker
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
            />
            
            {/* Sync Now Button - only for admins */}
            {isAdmin && (
              <Button
                variant="outline"
                onClick={handleSync}
                disabled={syncing}
                className="bg-blue-600/20 border-blue-500/50 text-blue-400 hover:bg-blue-600/30 hover:text-blue-300 gap-2"
              >
                {syncing ? (
                  <>
                    <ArrowsClockwise size={16} className="animate-spin" />
                    Синхронизация...
                  </>
                ) : (
                  <>
                    <Lightning size={16} />
                    Обновить сейчас
                  </>
                )}
              </Button>
            )}
            
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={refreshing}
              className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700"
            >
              <ArrowsClockwise className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* No Data Message */}
      {!hasSyncedData && !loading && (
        <div className="p-6">
          <div className="bg-zinc-800/50 rounded-xl p-8 border border-zinc-700/50 text-center">
            <Clock size={48} className="mx-auto mb-4 text-zinc-500" />
            <h2 className="text-lg font-semibold text-white mb-2">Данные не загружены</h2>
            <p className="text-zinc-400 mb-4">
              {isAdmin ? (
                <>
                  Нажмите &quot;Начать синхронизацию&quot; для первой загрузки данных из Ozon.
                  <br />
                  Это может занять несколько минут.
                </>
              ) : (
                <>
                  Данные загружаются автоматически каждый день в 01:00 по Москве.
                  <br />
                  Если это ваш первый вход — данные появятся после первой автоматической синхронизации.
                </>
              )}
            </p>
            {isAdmin && (
              <Button
                onClick={handleSync}
                disabled={syncing}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {syncing ? (
                  <>
                    <ArrowsClockwise size={16} className="mr-2 animate-spin" />
                    Синхронизация...
                  </>
                ) : (
                  <>
                    <Lightning size={16} className="mr-2" />
                    Начать синхронизацию
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      {(hasSyncedData || loading) && (
        <div className="p-6 space-y-6">
          {/* Top Row - Charts and Metrics */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            
            {/* Left Column - Bar Chart */}
            <div className="lg:col-span-4 bg-zinc-800/50 rounded-xl p-5 border border-zinc-700/50">
              <h2 className="text-sm font-medium text-zinc-400 mb-4">
                Выручка, затраты и маржинальная прибыль
              </h2>
              
              {loading ? (
                <Skeleton className="h-[280px] w-full bg-zinc-700" />
              ) : (
                <div>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={barChartData} barCategoryGap="25%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fill: '#9ca3af', fontSize: 11 }}
                        axisLine={{ stroke: '#374151' }}
                        tickLine={false}
                      />
                      <YAxis 
                        tick={{ fill: '#9ca3af', fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={formatMoneyShort}
                      />
                      <Tooltip content={<CustomBarTooltip />} />
                      <Bar 
                        dataKey="value" 
                        radius={[4, 4, 0, 0]}
                        fill="#3b82f6"
                      >
                        {barChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getBarFill(entry.name)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  
                  {/* Legend with values */}
                  <div className="flex justify-center gap-6 mt-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-blue-500" />
                      <span className="text-xs text-zinc-400">Выручка</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-purple-500" />
                      <span className="text-xs text-zinc-400">Итого затраты</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-green-500" />
                      <span className="text-xs text-zinc-400">Маржин. прибыль</span>
                    </div>
                  </div>
                  
                  {/* Values below legend */}
                  <div className="flex justify-between mt-4 px-2">
                    <div className="text-center">
                      <div className="text-sm font-semibold text-blue-400">{formatMoney(revenue)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-semibold text-purple-400">- {formatMoney(totalExpenses)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-semibold text-green-400">{formatMoney(profit)}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Middle Column - Metric Cards */}
            <div className="lg:col-span-3 space-y-4">
              {/* Marginal Profit Card */}
              <div className="bg-zinc-800/50 rounded-xl p-5 border border-zinc-700/50">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm text-zinc-400 mb-2">Маржинальная прибыль (МП)</div>
                    {loading ? (
                      <Skeleton className="h-8 w-32 bg-zinc-700" />
                    ) : (
                      <div className="text-2xl font-bold text-emerald-400">
                        {formatMoney(profit)}
                      </div>
                    )}
                  </div>
                  <Warning size={24} className="text-amber-500" />
                </div>
              </div>
              
              {/* Profitability Card */}
              <div className="bg-zinc-800/50 rounded-xl p-5 border border-zinc-700/50">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm text-zinc-400 mb-2">Рентабельность по МП</div>
                    {loading ? (
                      <Skeleton className="h-8 w-20 bg-zinc-700" />
                    ) : (
                      <div className="text-2xl font-bold text-emerald-400">
                        {formatPercent(profitMargin)}
                      </div>
                    )}
                  </div>
                  <Warning size={24} className="text-amber-500" />
                </div>
              </div>
            </div>

            {/* Right Column - Pie Chart */}
            <div className="lg:col-span-5 bg-zinc-800/50 rounded-xl p-5 border border-zinc-700/50">
              <h2 className="text-sm font-medium text-zinc-400 mb-4">
                Структура переменных затрат
              </h2>
              
              {loading ? (
                <Skeleton className="h-[280px] w-full bg-zinc-700" />
              ) : (
                <div className="flex items-start">
                  {/* Pie chart */}
                  <div className="w-2/5">
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={expensesData}
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {expensesData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => formatMoney(value)}
                          contentStyle={{
                            backgroundColor: "#27272a",
                            border: "1px solid #3f3f46",
                            borderRadius: "8px",
                            fontSize: "12px",
                            color: "#fff"
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  
                  {/* Legend */}
                  <div className="w-3/5 pl-4 text-xs space-y-2 flex flex-col justify-center">
                    {expensesData.map((item, index) => {
                      const percent = totalExpenses > 0 ? ((item.value / totalExpenses) * 100).toFixed(2) : 0;
                      return (
                        <div key={index} className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <div 
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                              style={{ backgroundColor: item.color }}
                            />
                            <span className="text-zinc-400 truncate">{item.name}</span>
                          </div>
                          <span className="text-white font-medium whitespace-nowrap text-right">
                            - {formatMoney(item.value)} / {percent}%
                          </span>
                        </div>
                      );
                    })}
                    
                    {/* Total */}
                    <div className="flex items-center justify-between gap-2 pt-2 border-t border-zinc-700 mt-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full bg-fuchsia-500" />
                        <span className="text-zinc-300 font-medium">Итого затраты</span>
                      </div>
                      <span className="text-white font-bold whitespace-nowrap">
                        - {formatMoney(totalExpenses)} / {revenue > 0 ? ((totalExpenses / revenue) * 100).toFixed(2) : 0}%
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Bottom Row - Product Issue Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Negative margin products */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-zinc-800/50 rounded-xl p-5 border-l-4 border-l-red-500 border border-zinc-700/50"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-white mb-2">Товары с отрицательной рентабельностью</h3>
                  {loading ? (
                    <Skeleton className="h-5 w-40 bg-zinc-700" />
                  ) : (
                    <div className="text-red-400 text-sm">
                      &lt; 0% — {metrics.negative_margin_count || 0} шт. {formatMoney(-(metrics.negative_margin_loss || 0))} убыток
                    </div>
                  )}
                </div>
                <Warning size={24} className="text-amber-500" />
              </div>
            </motion.div>

            {/* Products without cost price */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-zinc-800/50 rounded-xl p-5 border-l-4 border-l-amber-500 border border-zinc-700/50"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-white mb-2">Товары без себестоимости</h3>
                  {loading ? (
                    <Skeleton className="h-5 w-32 bg-zinc-700" />
                  ) : (
                    <div className="space-y-2">
                      <div className="text-amber-400 text-sm">
                        {metrics.no_cost_count || 0} шт.
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleDownloadTemplate}
                          disabled={downloadingTemplate}
                          className="h-7 px-2 text-xs bg-zinc-700/50 border-zinc-600 text-zinc-300 hover:bg-zinc-600 hover:text-white"
                        >
                          <DownloadSimple size={14} className="mr-1" />
                          {downloadingTemplate ? "Загрузка..." : "Скачать шаблон"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingCosts}
                          className="h-7 px-2 text-xs bg-blue-600/20 border-blue-500/50 text-blue-400 hover:bg-blue-600/30 hover:text-blue-300"
                        >
                          <UploadSimple size={14} className="mr-1" />
                          {uploadingCosts ? "Загрузка..." : "Загрузить"}
                        </Button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={handleUploadCosts}
                          className="hidden"
                        />
                      </div>
                    </div>
                  )}
                </div>
                <Warning size={24} className="text-amber-500" />
              </div>
            </motion.div>

            {/* Low margin products */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-zinc-800/50 rounded-xl p-5 border-l-4 border-l-orange-500 border border-zinc-700/50"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-white mb-2">Товары с низкой рентабельностью</h3>
                  {loading ? (
                    <Skeleton className="h-5 w-28 bg-zinc-700" />
                  ) : (
                    <div className="text-orange-400 text-sm">
                      &lt; 20% — {metrics.low_margin_count || 0} шт.
                    </div>
                  )}
                </div>
                <Warning size={24} className="text-amber-500" />
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </div>
  );
}
